import { useState, useEffect } from "react";
import React from "react";

const initialState = {
  name: "",
  email: "",
  phone: "",
  message: "",
  honeypot: "", // Hidden field for bot detection
};

export const Contact = (props) => {
  const [{ name, email, phone, message, honeypot }, setState] = useState(initialState);
  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState(""); // "success" or "error"
  const [formStartTime] = useState(Date.now()); // Track when form was loaded
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showPhoneHint, setShowPhoneHint] = useState(false);

  useEffect(() => {
    // Check for pre-populated message from promo pages
    const promoMessage = sessionStorage.getItem('promoMessage');
    if (promoMessage) {
      setState(prevState => ({ 
        ...prevState, 
        message: promoMessage 
      }));
      // Clear the stored message after using it
      sessionStorage.removeItem('promoMessage');
      // Store flag to indicate this came from a promo page
      sessionStorage.setItem('fromPromoPage', 'true');
    }

    // Load reCAPTCHA v3 script
    // Skip reCAPTCHA injection during react-snap / headless pre-rendering
    if (navigator.webdriver || window.location.hostname === 'localhost') return;
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF";
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Initialize reCAPTCHA when script loads
      window.grecaptcha.ready(() => {
        console.log('reCAPTCHA v3 loaded successfully');
      });
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };
    
    document.body.appendChild(script);
    
    // Cleanup function
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Only allow + as first character, then digits only
      const filtered = value.replace(/[^+\d]/g, '');
      const sanitized = filtered.charAt(0) === '+'
        ? '+' + filtered.slice(1).replace(/\+/g, '')
        : filtered.replace(/\+/g, '');
      // Block input if first character is not +
      if (sanitized.length > 0 && sanitized.charAt(0) !== '+') {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
        return;
      }
      if (sanitized !== value) {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
      }
      setState((prevState) => ({ ...prevState, phone: sanitized }));
    } else {
      setState((prevState) => ({ ...prevState, [name]: value }));
    }

    // Clear any previous messages when user starts typing
    if (submitMessage) {
      setSubmitMessage("");
      setSubmitStatus("");
    }
  };
  
  const clearState = () => setState({ ...initialState });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Phone format validation
    const trimmedPhone = phone.trim();
    if (trimmedPhone && !trimmedPhone.startsWith('+')) {
      setShowPhoneModal(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage("");
    setSubmitStatus("");

    try {
      // Check if reCAPTCHA is loaded
      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA not loaded');
      }

      // Execute reCAPTCHA v3
      console.log('DEBUG: Starting reCAPTCHA token generation...');
      const recaptchaToken = await new Promise((resolve, reject) => {
        window.grecaptcha.ready(async () => {
          try {
            console.log('DEBUG: reCAPTCHA ready, executing...');
            const token = await window.grecaptcha.execute('6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF', {action: 'submit'});
            console.log('DEBUG: reCAPTCHA token generated:', token ? 'SUCCESS' : 'FAILED');
            resolve(token);
          } catch (error) {
            console.error('DEBUG: reCAPTCHA execution failed:', error);
            reject(error);
          }
        });
      });
      
      // Prepare form data
      const formData = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        transactionalConsent: phone.trim() ? transactionalConsent : false,
        marketingConsent: phone.trim() ? marketingConsent : false,
        message: message.trim(),
        formStartTime, // For timing validation
        honeypot, // Should be empty for legitimate users
        recaptchaToken // Add reCAPTCHA token
      };

      // Make API call to our backend
      console.log('DEBUG: Making API request to /api/contact with data:', formData);
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('DEBUG: Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      console.log('DEBUG: Response JSON:', result);

      if (result.success) {
        setSubmitStatus("success");
        setSubmitMessage(result.message);
        clearState();
        setTransactionalConsent(false);
        setMarketingConsent(false);
        
        // Check if this submission came from a promo page
        const fromPromoPage = sessionStorage.getItem('fromPromoPage');
        if (fromPromoPage === 'true') {
          // Clear the flag
          sessionStorage.removeItem('fromPromoPage');
          
          // Close the window after a short delay to show the success message
          setTimeout(() => {
            // Try to close the window/tab
            window.close();
            
            // If window.close() doesn't work (common in modern browsers for security),
            // show a message to the user
            setTimeout(() => {
              if (!document.hidden) {
                alert('Thank you for your inquiry! You can now close this tab.');
              }
            }, 500);
          }, 2000); // 2 second delay to show success message
        }
      } else {
        setSubmitStatus("error");
        setSubmitMessage(result.message || "Failed to send message. Please try again.");
      }

    } catch (error) {
      console.error('DEBUG: Contact form error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        cause: error.cause
      });
      setSubmitStatus("error");
      setSubmitMessage(`Network error: ${error.message}. Please check your connection and try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div>
      <div id="contact" className="contact-section">
        <div className="container">
          <div className="col-md-8 col-md-offset-2">
            <div className="row">
              <div className="section-title">
                <h2>Get Started</h2>
                <p>
                  Your DX journey starts right here. Please call us on <a href="tel:+6498864638">
                  </a>
                  <span className="address-text">
                    <a href="tel:+6498864638">+64 9 886 4638</a>
                  </span>, or use the form below to send us an email and we will
                  get back to you as soon as possible. 
                </p>
              </div>
              <form name="sentMessage" validate onSubmit={handleSubmit}>
                {/* Honeypot field - hidden from users, bots will fill it */}
                <input 
                  type="text" 
                  name="honeypot" 
                  value={honeypot}
                  onChange={handleChange}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Name"
                        value={name}
                        required
                        disabled={isSubmitting}
                        onChange={handleChange}
                      />
                      <p className="help-block text-danger"></p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Email"
                        value={email}
                        required
                        disabled={isSubmitting}
                        onChange={handleChange}
                      />
                      <p className="help-block text-danger"></p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group" style={{position: 'relative'}}>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="form-control"
                        placeholder="Phone (optional)"
                        value={phone}
                        disabled={isSubmitting}
                        onChange={handleChange}
                      />
                      {showPhoneHint && (
                        <div style={{
                          position: 'absolute', top: '-38px', left: '0', right: '0',
                          backgroundColor: '#333', color: '#fff', fontSize: '12px',
                          padding: '6px 10px', borderRadius: '4px', textAlign: 'center',
                          zIndex: 10, whiteSpace: 'nowrap'
                        }}>
                          Only + followed by digits allowed
                          <div style={{
                            position: 'absolute', bottom: '-6px', left: '50%', marginLeft: '-6px',
                            width: 0, height: 0, borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent', borderTop: '6px solid #333'
                          }} />
                        </div>
                      )}
                      <p className="help-block text-danger"></p>
                    </div>
                  </div>
                </div>
                {phone.trim() && (
                  <>
                    <div style={{clear: 'both', overflow: 'hidden', marginTop: '-5px', marginBottom: '10px'}}>
                      <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                        <input
                          type="checkbox"
                          checked={transactionalConsent}
                          onChange={(e) => setTransactionalConsent(e.target.checked)}
                          disabled={isSubmitting}
                          style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                        />
                        <span style={{textAlign: 'justify'}}>
                          By checking this box, I consent to receive non-marketing text messages from Yuccan Technologies at the phone number provided. Messages may include responses to inquiries, appointment or consultation confirmations, service updates, and follow-up communications relevant to your request. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                        </span>
                      </label>
                    </div>
                    <div style={{clear: 'both', overflow: 'hidden', marginBottom: '15px'}}>
                      <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                        <input
                          type="checkbox"
                          checked={marketingConsent}
                          onChange={(e) => setMarketingConsent(e.target.checked)}
                          disabled={isSubmitting}
                          style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                        />
                        <span style={{textAlign: 'justify'}}>
                          By checking this box, I consent to receive marketing and promotional text messages from Yuccan Technologies at the phone number provided. Messages may include special offers, discounts, and new product updates among others. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                        </span>
                      </label>
                    </div>
                  </>
                )}
                <div className="form-group">
                  <textarea
                    name="message"
                    id="message"
                    className="form-control"
                    rows="4"
                    placeholder="Message (minimum 10 characters)"
                    value={message}
                    required
                    disabled={isSubmitting}
                    onChange={handleChange}
                  ></textarea>
                  <p className="help-block text-danger"></p>
                </div>
                
                {/* Success/Error Messages */}
                {submitMessage && (
                  <div className={`alert ${submitStatus === 'success' ? 'alert-success' : 'alert-danger'}`} style={{marginBottom: '20px'}}>
                    {submitMessage}
                  </div>
                )}
                
                {/* Privacy Notice */}
                <div className="privacy-notice" style={{margin: '15px 0', fontSize: '14px', color: '#fff'}}>
                  <p>
                    By submitting this form, you agree to our{" "}
                    <a href="/legal/terms-of-use.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                      Terms of Use
                    </a>
                    {" "}and{" "}
                    <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-custom btn-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
          <div className="col-md-12">
            <div className="row">
              <div className="social">
                <ul>
                  <li>
                    <a href={props.data ? props.data.facebook : "/"} title="Follow us on Facebook" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-facebook"></i>
                    </a>
                  </li>
                  <li>
                    <a href={props.data ? props.data.x : "/"} title="Follow us on X (formerly Twitter)" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-x"></i>
                    </a>
                  </li>
                  <li>
                    <a href={props.data ? props.data.youtube : "/"} title="Subscribe to our YouTube channel" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-youtube"></i>
                    </a>
                  </li>
                  <li>
                    <a href={props.data ? props.data.instagram : "/"} title="Follow us on Instagram" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-instagram"></i>
                    </a>
                  </li>
                  <li>
                    <a href={props.data ? props.data.tiktok : "/"} title="Follow us on TikTok" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-tiktok"></i>
                    </a>
                  </li>
                  <li>
                    <a href={props.data ? props.data.linkedin : "/"} title="Connect with us on LinkedIn" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-linkedin"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Phone Format Modal */}
      {showPhoneModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px', padding: '30px',
            maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h4 style={{ marginBottom: '15px', color: '#333' }}>Phone Format Required</h4>
            <p style={{ fontSize: '15px', color: '#555', marginBottom: '20px' }}>
              International format (<strong>+...</strong>) required.<br />
              Example: +1 555 123 4567
            </p>
            <button
              className="btn btn-custom"
              onClick={() => setShowPhoneModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
